/**
 * TEMP-H-011: Unified API Base Configuration
 * 
 * Single source of truth for all frontend API clients and hooks.
 * Default to live endpoint with environment override capability.
 */

// HISI-FE-AUTH-01: Using cookie auth instead of tokens

/**
 * Base URL for all backend API calls
 * Defaults to api.hi-si.ai with environment override capability
 */
console.log('🔍 ENV Check:', {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  NODE_ENV: import.meta.env.NODE_ENV,
  DEV: import.meta.env.DEV
});
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.hi-si.ai';

/**
 * API endpoints for different service areas
 */
export const API_ENDPOINTS = {
  // Execution endpoints
  executions: {
    list: (page = 1, limit = 10) => `/api/executions?page=${page}&limit=${limit}`,
    get: (id: string) => `/api/executions/${id}`,
    create: () => '/api/executions',
    update: (id: string) => `/api/executions/${id}`,
    delete: (id: string) => `/api/executions/${id}`,
    iterations: (id: string) => `/api/executions/${id}/iterations`,
    stages: (id: string) => `/api/executions/${id}/stages`,
    events: (id: string) => `/api/executions/${id}/events`,
  },
  
  // Stage settings endpoints  
  settings: {
    get: '/api/settings/stage',
    update: '/api/settings/stage',
    music: '/api/settings/music',
  },
  
  // QC and persona endpoints
  qc: {
    personas: '/api/qc/personas',
    thresholds: '/api/qc/thresholds',
    summary: (executionId: string) => `/api/qc/summary/${executionId}`,
  },
  
  // Authentication endpoints
  auth: {
    session: '/api/auth/session',
    login: '/api/auth/login',
    logout: '/api/auth/logout',
    refresh: '/api/auth/refresh',
    user: '/api/auth/user',
  },
  
  // Billing endpoints
  billing: {
    usage: '/api/billing/usage',
    limits: '/api/billing/limits',
    history: '/api/billing/history',
  },
  
  // Stage endpoints (for dependencies and other stage operations)
  stages: {
    dependencies: (stage: string) => `/api/stages/${stage}/dependencies`,
  },
  
  // WebSocket endpoints
  ws: {
    executions: '/ws/executions',
    pipeline: '/ws/pipeline',
  },

  // Demo Viewer endpoints (DV-INF-002)
  demo: {
    featureFlags: '/api/demo/feature-flags',
    billingCommits: (executionId: string) => `/api/demo/billing/commits?execution_id=${executionId}`,
    billingCalls: (commitId: string) => `/api/demo/billing/calls?commit_id=${commitId}`,
    storageOutputs: (executionId: string) => `/api/demo/storage/outputs?execution_id=${executionId}`,
    temporalEvents: (executionId: string, page = 1, filters?: any) => {
      const params = new URLSearchParams({
        execution_id: executionId,
        page: page.toString()
      });
      if (filters) {
        params.append('filters', JSON.stringify(filters));
      }
      return `/api/demo/temporal/events?${params}`;
    },
    temporalHeartbeats: (executionId: string) => `/api/demo/temporal/heartbeats?execution_id=${executionId}`,
    heartbeats: (executionId: string) => `/api/demo/temporal/heartbeats?execution_id=${executionId}`,
    qcSummary: (executionId: string, stage: string) => `/api/demo/qc/summary?execution_id=${executionId}&stage=${stage}`,
    authSession: '/api/demo/auth/session',
    authRefresh: '/api/demo/auth/refresh',
    cookiesEcho: '/api/demo/cookies/echo',
  },

  // Stage Tester endpoints (DV-STAGE-002)
  stageTester: {
    samples: (stage: string) => `/api/demo/stage/${stage}/samples`,
    prompt: (stage: string) => `/api/demo/stage/${stage}/prompt`,
    run: (stage: string) => `/api/demo/stage/${stage}/run`,
    iteration: (id: string) => `/api/demo/iterations/${id}`,
    lastOutput: (executionId: string, stage: string) => `/api/demo/executions/${executionId}/stages/${stage}/outputs`,
  }
} as const;

/**
 * Build full URL for API endpoint
 */
export function buildApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}

/**
 * Common headers for API requests
 * HISI-FE-AUTH-01: Cookie-based auth - no Authorization header
 * @deprecated Use getApiHeadersJson() for POST/PUT/PATCH or getApiHeadersGet() for GET requests
 */
export function getApiHeaders(_includeAuth = true): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // HISI-FE-AUTH-01: Remove Authorization header for cookie-based auth
  // Authentication is now handled via HttpOnly cookies sent with credentials:include
  // The includeAuth parameter is kept for compatibility but not used for headers

  return headers;
}

/**
 * Headers for GET requests (no Content-Type to avoid preflights)
 * DV-TEMP-004: Prevent CORS preflights on GET requests
 */
export function getApiHeadersGet(options?: { includeRequestId?: boolean }): HeadersInit {
  const headers: HeadersInit = {};

  // HISI-FE-AUTH-01: Cookie-based auth - no Authorization header needed
  // Authentication handled via HttpOnly cookies with credentials:include

  // DV-TEMP-004: X-Request-Id is optional and only included if explicitly requested
  // This prevents CORS errors when the server doesn't allow this header
  if (options?.includeRequestId) {
    // Note: This may cause CORS preflight if server doesn't allow X-Request-Id
    // Use with caution and only when server CORS configuration supports it
  }

  return headers;
}

/**
 * Headers for JSON requests (POST, PUT, PATCH with Content-Type)
 * DV-TEMP-004: Explicit JSON headers for write operations
 */
export function getApiHeadersJson(options?: { includeRequestId?: boolean }): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // HISI-FE-AUTH-01: Cookie-based auth - no Authorization header needed
  // Authentication handled via HttpOnly cookies with credentials:include

  // DV-TEMP-004: X-Request-Id is optional and only included if explicitly requested
  // This prevents CORS errors when the server doesn't allow this header
  if (options?.includeRequestId) {
    // Note: This may cause CORS preflight if server doesn't allow X-Request-Id
    // Use with caution and only when server CORS configuration supports it
  }

  return headers;
}

/**
 * Alias for getApiHeadersJson() - Standardized naming for POST operations
 * Use this for POST, PUT, PATCH requests that send JSON
 */
export const getApiHeadersPost = getApiHeadersJson;


/**
 * Default fetch configuration for API calls
 */
export const API_CONFIG = {
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
} as const;

/**
 * Environment information
 */
export const API_ENV = {
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  baseUrl: API_BASE_URL,
} as const;

// Debug logging in development
if (API_ENV.isDevelopment) {
  console.log('🚀 API Configuration:');
  console.log('  Base URL:', API_BASE_URL);
  console.log('  Environment:', API_ENV.isDevelopment ? 'Development' : 'Production');
  console.log('  Available endpoints:', Object.keys(API_ENDPOINTS));
}